import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import { KycStatus } from '@/features/auth/interfaces/kyc.interface';

export interface AuthState {
  authenticated?: boolean;
  kyc?: KycStatus | null;
  email?: string | null;
  alias?: string | null;
  id?: string | null;
  referrerCode?: string;
  firstName?: string | null;
  lastName?: string | null;
  isAgent?: boolean;
  isAmbassador?: boolean;
  phone?: string | null;
  countryCode?: string | null;
  loading?: boolean;
  otpEnabled?: boolean;
  otpLastUsedAt?: string | null;
  mfaRequired?: boolean;
  mfaSetupRequired?: boolean;
  mfaContext?: string | null;
  pendingAction?: any;
  locked?: boolean;
  qrCode?: string | null;
  totpSecret?: string | null;
  totpUri?: string | null;
  allowSkipMfa?: boolean;
}

const initialState: AuthState = {
  authenticated: false,
  kyc: null,
  email: null,
  loading: false,
  otpEnabled: false,
  otpLastUsedAt: null,
  mfaRequired: false,
  mfaSetupRequired: false,
  mfaContext: null,
  pendingAction: null,
  locked: false,
  allowSkipMfa: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.authenticated = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setUserData: (state, action: PayloadAction<Partial<AuthState>>) => {
      Object.assign(state, action.payload);
      state.loading = false;
    },
    setMfaRequired: (state) => {
      state.mfaRequired = true;
    },
    setMfaSetupRequired: (state, action: PayloadAction<{ qrCode: string; totpSecret: string; totpUri: string }>) => {
      state.mfaSetupRequired = true;
      state.qrCode = action.payload.qrCode;
      state.totpSecret = action.payload.totpSecret;
      state.totpUri = action.payload.totpUri;
    },
    clearMfaRequired: (state) => {
      state.mfaRequired = false;
      state.mfaSetupRequired = false;
      state.qrCode = null;
      state.totpSecret = null;
      state.totpUri = null;
      state.mfaContext = null;
      state.pendingAction = null;
    },
    logout: () => {
      return initialState;
    },
    enableMfaUserLock: (state) => {
      state.locked = true;
    },
    promptMfaRequired: (state, action: PayloadAction<{ allowSkip?: boolean }>) => {
      state.allowSkipMfa = action.payload?.allowSkip ?? false;
    },
  },
});

export const otpCodeSubmit = createAction<string>('otpCode/submit');
export const promptMfaRequired = createAction<{ allowSkip?: boolean }>('mfa/prompt');

export const {
  setAuthenticated,
  setLoading,
  setUserData,
  setMfaRequired,
  setMfaSetupRequired,
  clearMfaRequired,
  logout,
  enableMfaUserLock,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
